import React from 'react';
import { Box, Typography, Stack, Alert } from '@mui/material';
import { useQuery } from '@apollo/client';
import { CustomFieldSectionCard } from './CustomFieldSectionCard';
import { DeleteCustomFieldSectionDialog } from './DeleteCustomFieldSectionDialog';
import { CreateSectionForm } from './CreateSectionForm';
import { GET_CUSTOM_FIELD_SECTIONS } from './customFieldsQueries';
import { useCustomFieldSections } from '../hooks/useCustomFieldSections';

export const CustomFieldsTab: React.FC = () => {
  const { data, loading, error } = useQuery(GET_CUSTOM_FIELD_SECTIONS);
  const {
    sections,
    saveStatuses,
    deleteSectionId,
    setDeleteSectionId,
    handleCreateSection,
    handleDeleteSection,
    handleConfirmDeleteSection,
    handleSaveSection,
    handleTogglePublish,
    handleUpdateSectionTitle,
    handleAddField,
    handleDeleteField,
    handleUpdateField,
    handleAddOption,
    handleRemoveOption,
  } = useCustomFieldSections(data);

  if (loading) return <Typography>Loading custom data fields...</Typography>;
  if (error) return <Alert severity="error">Failed to load custom fields: {error.message}</Alert>;

  if (sections.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Define custom sections and metric fields. These will dynamically appear in your Daily Metrics Form.
        </Typography>
        <CreateSectionForm onCreate={handleCreateSection} />
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No sections yet. Create your first section above.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define custom sections and metric fields. These will dynamically appear in your Daily Metrics Form.
      </Typography>

      <CreateSectionForm onCreate={handleCreateSection} />

      <Stack spacing={4}>
        {sections.map((section) => (
          <CustomFieldSectionCard
            key={section.id}
            section={section}
            onUpdateSectionTitle={handleUpdateSectionTitle}
            onAddField={handleAddField}
            onSaveSection={handleSaveSection}
            onDeleteSection={handleDeleteSection}
            onUpdateField={handleUpdateField}
            onDeleteField={handleDeleteField}
            onAddOption={handleAddOption}
            onRemoveOption={handleRemoveOption}
            onTogglePublish={handleTogglePublish}
            saveStatus={saveStatuses[section.id] || 'idle'}
          />
        ))}
      </Stack>

      <DeleteCustomFieldSectionDialog
        open={!!deleteSectionId}
        onConfirm={handleConfirmDeleteSection}
        onCancel={() => setDeleteSectionId(null)}
      />
    </Box>
  );
};
